import React, { useState, useEffect } from 'react';
import { Share2, X, Plus, Trash2, User, Loader2 } from 'lucide-react';
import { shareProject, unshareProject, loadProjectFromCloud } from '@/lib/cloud-storage';
import { Project } from '@/lib/storage';
import { useAuth } from '@/ui/auth/AuthProvider';

interface ShareModalProps {
    projectId: string;
    onClose: () => void;
}

export function ShareModal({ projectId, onClose }: ShareModalProps) {
    const { user } = useAuth();
    const [email, setEmail] = useState('');
    const [project, setProject] = useState<Project | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSharing, setIsSharing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadProject();
    }, [projectId]);

    const loadProject = async () => {
        try {
            setIsLoading(true);
            const p = await loadProjectFromCloud(projectId);
            setProject(p);
        } catch (err) {
            setError('Failed to load project details');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleShare = async () => {
        if (!email.trim()) return;

        try {
            setIsSharing(true);
            setError(null);
            await shareProject(projectId, email.trim());
            setEmail('');
            await loadProject(); // Reload to show new share
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsSharing(false);
        }
    };

    const handleUnshare = async (emailToRemove: string) => {
        try {
            setError(null);
            await unshareProject(projectId, emailToRemove);
            await loadProject();
        } catch (err) {
            setError((err as Error).message);
        }
    };

    if (!user) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-background-secondary border border-border rounded-lg w-full max-w-md overflow-hidden flex flex-col shadow-2xl">

                {/* Header */}
                <div className="p-4 border-b border-border flex items-center justify-between bg-background-tertiary/50">
                    <div className="flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-accent" />
                        <h2 className="font-bold">Share Project</h2>
                    </div>
                    <button onClick={onClose} className="text-foreground-muted hover:text-foreground">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <>
                            {/* Add User */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Add people</label>
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        placeholder="Enter email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm focus:border-accent outline-none"
                                        onKeyDown={(e) => e.key === 'Enter' && handleShare()}
                                    />
                                    <button
                                        onClick={handleShare}
                                        disabled={isSharing || !email.trim()}
                                        className="btn-primary px-3 py-2 flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                        Share
                                    </button>
                                </div>
                            </div>

                            {/* List */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-foreground-muted">People with access</h3>

                                <div className="space-y-2">
                                    {/* Owner */}
                                    <div className="flex items-center justify-between p-2 rounded hover:bg-background-tertiary/50">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                                                <User className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">Owner</div>
                                                <div className="text-xs text-foreground-muted">Project Creator</div>
                                            </div>
                                        </div>
                                        <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded">Owner</span>
                                    </div>

                                    {/* Shared Users */}
                                    {project?.sharedWith?.map((sharedEmail) => (
                                        <div key={sharedEmail} className="flex items-center justify-between p-2 rounded hover:bg-background-tertiary/50">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <div className="text-sm">{sharedEmail}</div>
                                            </div>

                                            {project.userId === user.uid && (
                                                <button
                                                    onClick={() => handleUnshare(sharedEmail)}
                                                    className="text-red-400 hover:bg-red-500/10 p-1.5 rounded transition-colors"
                                                    title="Remove access"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    {(!project?.sharedWith || project.sharedWith.length === 0) && (
                                        <div className="text-center py-4 text-sm text-foreground-muted italic">
                                            Not shared with anyone yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-3 rounded">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
